import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LeagueStandings from './components/LeagueStandings';
import UpcomingFixtures from './components/UpcomingFixtures';
import RecentResults from './components/RecentResults';
import SearchAndFilters from './components/SearchAndFilters';
import { matchService } from '../../services/matchService';
import { leagueService } from '../../services/leagueService';
import { playerService } from '../../services/playerService';
import { tournamentService } from '../../services/tournamentService';
import { useAuth } from '../../contexts/AuthContext';
import { filterPublicCompetitions } from '../../utils/publicCompetitionVisibility';

const PublicLeaguePortal = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading, isAuthenticated } = useAuth();
  const [selectedSeason, setSelectedSeason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [competitionType, setCompetitionType] = useState('league'); // 'league' | 'tournament'
  
  // Data states
  const [upcomingFixtures, setUpcomingFixtures] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [standingsData, setStandingsData] = useState({});
  const [leagueStats, setLeagueStats] = useState({
    totalGamesPlayed: 0,
    averagePointsPerGame: 0,
    topScorer: null,
    teamsCompeting: 0,
    venuesCount: 0
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStandings, setIsLoadingStandings] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [error, setError] = useState(null);

  // Helper to format date
  const formatShortDate = (isoDate) => {
    if (!isoDate) return 'N/A';
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Calculate team form from recent matches (last 5 games: W/L/D)
  const calculateTeamForm = (teamId, allMatches) => {
    if (!allMatches || !teamId) return '-----';
    
    const now = new Date();
    
    // Get team's completed matches, sorted by scheduled date (most recent first)
    const teamMatches = allMatches
      .filter(match => {
        const isTeamMatch = match.homeTeamId === teamId || match.awayTeamId === teamId;
        const hasScores = match.homeScore !== null && match.homeScore !== undefined &&
                         match.awayScore !== null && match.awayScore !== undefined;
        const matchDate = match.scheduledDate ? new Date(match.scheduledDate) : null;
        const matchDayHasPassed = matchDate ? matchDate < now : false;
        const isCompleted = match.matchStatus === 'completed';
        
        return isTeamMatch && hasScores && (matchDayHasPassed || isCompleted);
      })
      .sort((a, b) => {
        const dateA = a.scheduledDate ? new Date(a.scheduledDate) : new Date(0);
        const dateB = b.scheduledDate ? new Date(b.scheduledDate) : new Date(0);
        return dateB - dateA; // Most recent first
      })
      .slice(0, 5); // Last 5 matches
    
    if (teamMatches.length === 0) return '-----';
    
    // Build form string (W = Win, L = Loss, D = Draw/Tie)
    const form = teamMatches.map(match => {
      const isHome = match.homeTeamId === teamId;
      const teamScore = isHome ? match.homeScore : match.awayScore;
      const opponentScore = isHome ? match.awayScore : match.homeScore;
      
      if (teamScore > opponentScore) return 'W';
      if (teamScore < opponentScore) return 'L';
      return 'D'; // Draw/Tie
    }).join('');
    
    // Pad with '-' if less than 5 matches
    return form.padEnd(5, '-');
  };

  const loadStandings = async (leagueId) => {
    setIsLoadingStandings(true);
    try {
      // Fetch all matches for this league first (needed for stats calculation)
      let allMatches = [];
      try {
        allMatches = await matchService.getAll();
        // Filter by league
        allMatches = allMatches.filter(match => match.leagueId === leagueId);
      } catch (err) {
        console.warn('Could not fetch matches for standings calculation:', err);
      }
      
      // Helper function to extract specific stage from match notes
      const getMatchStage = (matchNotes) => {
        if (!matchNotes) return 'Group Stage';
        const notes = matchNotes.toLowerCase();
        
        // Check for specific stages (order matters - check more specific first)
        if (notes.includes('third place')) return 'Third Place';
        if (notes.includes('classification')) return 'Classification';
        if (notes.includes('quarter finals') || notes.includes('quarter-finals')) return 'Quarter Finals';
        if (notes.includes('semi finals') || notes.includes('semi-finals')) return 'Semi Finals';
        if (notes.includes('finals') && !notes.includes('semi') && !notes.includes('quarter')) return 'Finals';
        if (notes.includes('knockout bracket')) {
          // Try to extract stage from notes format: "Knockout Bracket - Stage Name"
          const stageMatch = matchNotes.match(/Knockout Bracket - ([^-]+)/);
          if (stageMatch) {
            const stageName = stageMatch[1].trim();
            // Normalize stage names
            const normalized = stageName.toLowerCase();
            if (normalized.includes('quarter')) return 'Quarter Finals';
            if (normalized.includes('semi')) return 'Semi Finals';
            if (normalized.includes('third')) return 'Third Place';
            if (normalized.includes('classification')) return 'Classification';
            if (normalized.includes('final')) return 'Finals';
            return stageName; // Return as-is if not recognized
          }
          return 'Knockout'; // Generic knockout if can't parse
        }
        return 'Group Stage'; // Default to group stage
      };

      // Calculate statistics from matches organized by stage
      const now = new Date();
      const standingsByStage = new Map(); // Map<stage, Array<teamStandings>>
      
      // Define stage order
      const stageOrder = ['Group Stage', 'Quarter Finals', 'Semi Finals', 'Third Place', 'Classification', 'Finals'];
      
      // Initialize standings for each stage
      stageOrder.forEach(stage => {
        standingsByStage.set(stage, new Map()); // Map<teamId, stats>
      });
      
      // Process all completed matches to calculate team statistics by stage
      allMatches
        .filter(match => {
          const hasScores = match.homeScore !== null && match.homeScore !== undefined &&
                          match.awayScore !== null && match.awayScore !== undefined;
          const matchDate = match.scheduledDate ? new Date(match.scheduledDate) : null;
          const matchDayHasPassed = matchDate ? matchDate < now : false;
          const isCompleted = match.matchStatus === 'completed';
          return hasScores && (matchDayHasPassed || isCompleted);
        })
        .forEach(match => {
          const homeTeamId = match.homeTeamId;
          const awayTeamId = match.awayTeamId;
          const homeScore = Number(match.homeScore) || 0;
          const awayScore = Number(match.awayScore) || 0;
          const matchStage = getMatchStage(match.matchNotes);
          
          // Get or create stage stats map
          const stageStatsMap = standingsByStage.get(matchStage) || new Map();
          
          // Initialize home team stats for this stage if not exists
          if (!stageStatsMap.has(homeTeamId)) {
            stageStatsMap.set(homeTeamId, {
              teamId: homeTeamId,
              gamesPlayed: 0,
              wins: 0,
              losses: 0,
              pointsFor: 0,
              pointsAgainst: 0,
              team: match.homeTeam
            });
          }
          
          // Initialize away team stats for this stage if not exists
          if (!stageStatsMap.has(awayTeamId)) {
            stageStatsMap.set(awayTeamId, {
              teamId: awayTeamId,
              gamesPlayed: 0,
              wins: 0,
              losses: 0,
              pointsFor: 0,
              pointsAgainst: 0,
              team: match.awayTeam
            });
          }
          
          // Update home team stats for this stage
          const homeStats = stageStatsMap.get(homeTeamId);
          homeStats.gamesPlayed += 1;
          homeStats.pointsFor += homeScore;
          homeStats.pointsAgainst += awayScore;
          if (homeScore > awayScore) {
            homeStats.wins += 1;
          } else if (homeScore < awayScore) {
            homeStats.losses += 1;
          }
          
          // Update away team stats for this stage
          const awayStats = stageStatsMap.get(awayTeamId);
          awayStats.gamesPlayed += 1;
          awayStats.pointsFor += awayScore;
          awayStats.pointsAgainst += homeScore;
          if (awayScore > homeScore) {
            awayStats.wins += 1;
          } else if (awayScore < homeScore) {
            awayStats.losses += 1;
          }
          
          // Update the stage map
          standingsByStage.set(matchStage, stageStatsMap);
        });
      
      // Transform each stage's stats into standings arrays
      const transformedStandingsByStage = new Map();
      
      stageOrder.forEach(stage => {
        const stageStatsMap = standingsByStage.get(stage) || new Map();
        
        // Convert map to array and calculate additional stats
        const stageStandings = Array.from(stageStatsMap.values()).map((stats) => {
          const pointDifference = stats.pointsFor - stats.pointsAgainst;
          const wins = stats.wins || 0;
          const losses = stats.losses || 0;
          // FIBA points: (wins * 2) + (losses * 1)
          const leaguePoints = (wins * 2) + (losses * 1);
          
          // Get team info
          const teamInfo = stats.team || {
            id: stats.teamId,
            name: 'Unknown Team',
            shortName: 'N/A',
            logoUrl: '/assets/images/no_image.png'
          };
          
          return {
            id: stats.teamId,
            name: teamInfo.name || 'Unknown Team',
            shortName: teamInfo.shortName || 'N/A',
            logo: teamInfo.logoUrl || '/assets/images/no_image.png',
            logoAlt: `${teamInfo.name || 'Team'} logo`,
            gamesPlayed: stats.gamesPlayed || 0,
            wins: wins,
            losses: losses,
            points: leaguePoints,
            pointsDiff: pointDifference,
            pointsFor: stats.pointsFor || 0,
            pointsAgainst: stats.pointsAgainst || 0,
            form: calculateTeamForm(stats.teamId, allMatches) || '-----',
            position: null, // Will be set after sorting
            positionChange: 0
          };
        });
        
        // Sort standings by points (descending), then point difference (descending)
        stageStandings.sort((a, b) => {
          if (a.points !== b.points) {
            return b.points - a.points;
          }
          return (b.pointsDiff || 0) - (a.pointsDiff || 0);
        });
        
        // Assign positions
        stageStandings.forEach((standing, index) => {
          standing.position = index + 1;
        });
        
        // Only add stage if it has teams
        if (stageStandings.length > 0) {
          transformedStandingsByStage.set(stage, stageStandings);
        }
      });
      
      // Convert to object format for easier access in component
      // This structure ensures standings are organized by stage (Group Stage, Quarter Finals, etc.)
      const standingsData = {};
      transformedStandingsByStage.forEach((standings, stage) => {
        standingsData[stage] = standings;
      });
      
      // Always set as object (even if empty) to ensure stage-organized view is used
      setStandingsData(standingsData);
      
      // Debug: Log the structure to verify it's correct
      if (Object.keys(standingsData).length > 0) {
        console.log('Standings organized by stage:', Object.keys(standingsData));
      }
    } catch (err) {
      console.error('Error loading standings:', err);
      setError(err.message || 'Failed to load standings');
    } finally {
      setIsLoadingStandings(false);
    }
  };

  const homeScoreSafe = (value) => Number(value) || 0;

  const loadFixturesAndResults = async (options = {}) => {
    const leagueId = typeof options === 'string' || options === null ? options : options.leagueId;
    const tournamentId = typeof options === 'object' && options ? options.tournamentId : null;
    setIsLoading(true);
    setError(null);
    try {
      let allMatches = [];

      if (tournamentId) {
        const groups = await tournamentService.getGroups(tournamentId);
        const groupNameById = new Map(
          (groups || []).map((group) => [group.id, group.name])
        );
        const games = await tournamentService.getGamesByTournament(tournamentId);
        allMatches = (games || []).map((game) => ({
          id: game.id,
          scheduledDate: game.scheduledDate,
          venue: game.venue,
          matchStatus: game.gameStatus || 'scheduled',
          homeScore: game.homeScore,
          awayScore: game.awayScore,
          homeTeamId: game.homeTeamId,
          awayTeamId: game.awayTeamId,
          groupId: game.groupId || null,
          quarter: null,
          matchNotes: game.matchNotes || game.stageName || null,
          homeTeam: {
            name: game.homeTeam?.teamName || 'TBD',
            shortName: game.homeTeam?.teamName?.substring(0, 3)?.toUpperCase() || 'TBD',
            logoUrl: game.homeTeam?.logoUrl,
          },
          awayTeam: {
            name: game.awayTeam?.teamName || 'TBD',
            shortName: game.awayTeam?.teamName?.substring(0, 3)?.toUpperCase() || 'TBD',
            logoUrl: game.awayTeam?.logoUrl,
          },
          stageName: game.stageName || 'Group / Stage',
          groupName: game.groupId ? groupNameById.get(game.groupId) || null : null,
        }));
      } else {
        allMatches = await matchService.getAll();
        if (leagueId) {
          allMatches = allMatches.filter((match) => match.leagueId === leagueId);
        }
      }

      const now = new Date();

      const upcoming = allMatches
        .filter((match) => {
          const matchDate = match.scheduledDate ? new Date(match.scheduledDate) : null;
          return (
            matchDate &&
            matchDate >= now &&
            (match.matchStatus === 'scheduled' || match.matchStatus === 'live')
          );
        })
        .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
        .slice(0, 10);

      const transformedFixtures = upcoming.map((match) => ({
        id: match.id,
        dateTime: match.scheduledDate,
        homeTeam: {
          name: match.homeTeam?.name || 'TBD',
          logo: match.homeTeam?.logoUrl || '/assets/images/no_image.png',
          logoAlt: `${match.homeTeam?.name || 'Team'} logo`,
          record: 'N/A',
        },
        awayTeam: {
          name: match.awayTeam?.name || 'TBD',
          logo: match.awayTeam?.logoUrl || '/assets/images/no_image.png',
          logoAlt: `${match.awayTeam?.name || 'Team'} logo`,
          record: 'N/A',
        },
        venue: match.venue || 'TBD',
        status: match.matchStatus || 'scheduled',
        stageName: match.stageName || null,
      }));

      const completed = allMatches
        .filter((match) => {
          const hasScores =
            match.homeScore !== null &&
            match.homeScore !== undefined &&
            match.awayScore !== null &&
            match.awayScore !== undefined;
          const matchDate = match.scheduledDate ? new Date(match.scheduledDate) : null;
          const matchDayHasPassed = matchDate ? matchDate < now : false;
          const isCompleted = match.matchStatus === 'completed';
          return hasScores && (matchDayHasPassed || isCompleted);
        })
        .sort((a, b) => {
          const dateA = a.scheduledDate ? new Date(a.scheduledDate) : new Date(0);
          const dateB = b.scheduledDate ? new Date(b.scheduledDate) : new Date(0);
          return dateB - dateA;
        });

      const transformedResults = completed.map((match) => {
        const homeScore = match.homeScore || 0;
        const awayScore = match.awayScore || 0;
        const scoreDiff = Math.abs(homeScore - awayScore);
        const totalScore = homeScore + awayScore;

        let highlight = null;
        if (scoreDiff <= 3) highlight = 'close';
        else if (totalScore >= 180) highlight = 'high-scoring';

        const isOvertime =
          (match.quarter && typeof match.quarter === 'number' && match.quarter > 4) ||
          (match.matchNotes &&
            typeof match.matchNotes === 'string' &&
            (match.matchNotes.toLowerCase().includes('overtime') ||
              match.matchNotes.toLowerCase().includes(' ot ')));

        return {
          id: match.id,
          date: match.scheduledDate,
          sourceType: tournamentId ? 'tournament' : 'league',
          homeTeam: {
            name: match.homeTeam?.name || 'TBD',
            shortName:
              match.homeTeam?.shortName ||
              match.homeTeam?.name?.substring(0, 3).toUpperCase() ||
              'TBD',
            logo: match.homeTeam?.logoUrl || '/assets/images/no_image.png',
            logoAlt: `${match.homeTeam?.name || 'Team'} logo`,
          },
          awayTeam: {
            name: match.awayTeam?.name || 'TBD',
            shortName:
              match.awayTeam?.shortName ||
              match.awayTeam?.name?.substring(0, 3).toUpperCase() ||
              'TBD',
            logo: match.awayTeam?.logoUrl || '/assets/images/no_image.png',
            logoAlt: `${match.awayTeam?.name || 'Team'} logo`,
          },
          homeScore,
          awayScore,
          venue: match.venue || 'TBD',
          overtime: isOvertime,
          highlight,
          stageName: match.stageName || null,
        };
      });

      setUpcomingFixtures(transformedFixtures);
      setRecentResults(transformedResults);

      if (tournamentId) {
        const byStage = {};
        completed.forEach((match) => {
          const stage =
            match.groupName ||
            (match.groupId ? `Group ${match.groupId}` : null) ||
            match.stageName ||
            'Overall';
          if (!byStage[stage]) byStage[stage] = {};
          const ensure = (teamId, teamName, shortName) => {
            const key = teamId || teamName;
            if (!byStage[stage][key]) {
              byStage[stage][key] = {
                team: {
                  id: key,
                  name: teamName,
                  shortName: shortName || teamName?.substring(0, 3)?.toUpperCase(),
                },
                gamesPlayed: 0,
                wins: 0,
                losses: 0,
                pointsFor: 0,
                pointsAgainst: 0,
                points: 0,
                form: '-----',
              };
            }
            return byStage[stage][key];
          };
          const home = ensure(
            match.homeTeamId,
            match.homeTeam?.name || 'Home',
            match.homeTeam?.shortName
          );
          const away = ensure(
            match.awayTeamId,
            match.awayTeam?.name || 'Away',
            match.awayTeam?.shortName
          );
          home.gamesPlayed += 1;
          away.gamesPlayed += 1;
          home.pointsFor += homeScoreSafe(match.homeScore);
          home.pointsAgainst += homeScoreSafe(match.awayScore);
          away.pointsFor += homeScoreSafe(match.awayScore);
          away.pointsAgainst += homeScoreSafe(match.homeScore);
          if (homeScoreSafe(match.homeScore) > homeScoreSafe(match.awayScore)) {
            home.wins += 1;
            away.losses += 1;
            home.points += 2;
            away.points += 1;
          } else if (homeScoreSafe(match.awayScore) > homeScoreSafe(match.homeScore)) {
            away.wins += 1;
            home.losses += 1;
            away.points += 2;
            home.points += 1;
          } else {
            home.points += 1;
            away.points += 1;
          }
        });

        const standings = {};
        Object.entries(byStage).forEach(([stage, teamsMap]) => {
          standings[stage] = Object.values(teamsMap)
            .map((row) => ({
              id: row.team?.id || row.teamId || row.team?.name,
              name: row.team?.name || 'Unknown Team',
              shortName: row.team?.shortName || row.team?.name?.substring(0, 3)?.toUpperCase() || 'N/A',
              logo: row.team?.logoUrl || '/assets/images/no_image.png',
              logoAlt: `${row.team?.name || 'Team'} logo`,
              gamesPlayed: row.gamesPlayed || 0,
              wins: row.wins || 0,
              losses: row.losses || 0,
              points: row.points || 0,
              pointDifference: (row.pointsFor || 0) - (row.pointsAgainst || 0),
              pointsDiff: (row.pointsFor || 0) - (row.pointsAgainst || 0),
              form: row.form || '-----',
            }))
            .sort((a, b) => b.points - a.points || b.pointDifference - a.pointDifference);
        });
        setStandingsData(standings);

        const venues = new Set(allMatches.map((m) => m.venue).filter(Boolean));
        setLeagueStats({
          totalGamesPlayed: completed.length,
          averagePointsPerGame: completed.length
            ? Number(
                (
                  completed.reduce(
                    (sum, m) => sum + homeScoreSafe(m.homeScore) + homeScoreSafe(m.awayScore),
                    0
                  ) / completed.length
                ).toFixed(1)
              )
            : 0,
          topScorer: null,
          teamsCompeting: new Set(
            allMatches.flatMap((m) => [m.homeTeamId, m.awayTeamId]).filter(Boolean)
          ).size,
          venuesCount: venues.size,
        });
      }
    } catch (err) {
      console.error('Error loading fixtures and results:', err);
      setError(err.message || 'Failed to load fixtures and results');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLeagueStatistics = async (leagueId) => {
    setIsLoadingStats(true);
    try {
      const allMatches = await matchService.getAll();
      const leagueMatches = allMatches.filter(match => match.leagueId === leagueId);
      
      const completedMatches = leagueMatches.filter(match => 
        match.matchStatus === 'completed' && 
        match.homeScore !== null && 
        match.awayScore !== null
      );
      const totalGamesPlayed = completedMatches.length;
      
      let averagePointsPerGame = 0;
      if (completedMatches.length > 0) {
        const totalPoints = completedMatches.reduce((sum, match) => {
          return sum + (match.homeScore || 0) + (match.awayScore || 0);
        }, 0);
        averagePointsPerGame = Number((totalPoints / completedMatches.length).toFixed(1));
      }
      
      let topScorer = null;
      try {
        const playersWithStats = await playerService.getPlayersWithStatistics({ 
          leagueId: leagueId 
        });
        
        if (playersWithStats && playersWithStats.length > 0) {
          const topPlayer = playersWithStats.reduce((max, player) => {
            const playerPPG = player.stats?.pointsPerGame || 0;
            const maxPPG = max.stats?.pointsPerGame || 0;
            return playerPPG > maxPPG ? player : max;
          }, playersWithStats[0]);
          
          if (topPlayer && topPlayer.stats?.pointsPerGame > 0) {
            const playerName = topPlayer.name || 'Unknown Player';
            const shortName = playerName.split(' ').map(n => n[0]).join('. ') || playerName.substring(0, 10);
            topScorer = `${shortName} (${topPlayer.stats.pointsPerGame} PPG)`;
          }
        }
      } catch (err) {
        console.error('Error loading top scorer:', err);
      }
      
      const selectedLeague = leagues.find(l => l.id === leagueId);
      const teamsCompeting = selectedLeague?.currentTeams || 0;
      
      const uniqueVenues = new Set(
        leagueMatches
          .filter(match => match.venue)
          .map(match => match.venue)
      );
      const venuesCount = uniqueVenues.size;
      
      setLeagueStats({
        totalGamesPlayed,
        averagePointsPerGame,
        topScorer,
        teamsCompeting,
        venuesCount
      });
    } catch (err) {
      console.error('Error loading league statistics:', err);
      setLeagueStats({
        totalGamesPlayed: 0,
        averagePointsPerGame: 0,
        topScorer: null,
        teamsCompeting: 0,
        venuesCount: 0
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  const loadCompetitions = async () => {
    try {
      const [leaguesData, tournamentsData] = await Promise.all([
        leagueService.getAll(),
        tournamentService.getTournaments(),
      ]);
      const visible = filterPublicCompetitions(leaguesData || []);
      const visibleTournaments = filterPublicCompetitions(tournamentsData || []);
      setLeagues(visible);
      setTournaments(visibleTournaments);

      if (visible.length > 0) {
        const firstLeague = visible[0];
        setCompetitionType('league');
        setSelectedLeagueId(firstLeague.id);
        setSelectedTournamentId('');
        setSelectedSeason(firstLeague.season || '');
        loadLeagueStatistics(firstLeague.id);
      } else if (visibleTournaments.length > 0) {
        const firstTournament = visibleTournaments[0];
        setCompetitionType('tournament');
        setSelectedTournamentId(firstTournament.id);
        setSelectedLeagueId('');
        setSelectedSeason(firstTournament.season || '');
      } else {
        setSelectedLeagueId('');
        setSelectedTournamentId('');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error loading competitions:', err);
      setError(err.message || 'Failed to load competitions');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'Public League Portal - TBF Registration System';
    loadCompetitions();
  }, []);

  useEffect(() => {
    if (competitionType === 'league' && selectedLeagueId) {
      loadFixturesAndResults({ leagueId: selectedLeagueId });
    } else if (competitionType === 'tournament' && selectedTournamentId) {
      loadFixturesAndResults({ tournamentId: selectedTournamentId });
    }
  }, [competitionType, selectedLeagueId, selectedTournamentId]);

  useEffect(() => {
    if (competitionType === 'league' && selectedLeagueId) {
      const selected = leagues.find((l) => String(l.id) === String(selectedLeagueId));
      if (selected) {
        setSelectedSeason(selected.season || '');
      }
      loadStandings(selectedLeagueId);
      loadLeagueStatistics(selectedLeagueId);
    } else if (competitionType === 'tournament' && selectedTournamentId) {
      const selected = tournaments.find((t) => String(t.id) === String(selectedTournamentId));
      if (selected) {
        setSelectedSeason(selected.season || '');
      }
    }
  }, [competitionType, selectedLeagueId, selectedTournamentId, leagues, tournaments]);

  const handleLeagueChange = (value) => {
    if (!value) {
      setSelectedLeagueId('');
      return;
    }
    setCompetitionType('league');
    setSelectedLeagueId(value);
    setSelectedTournamentId('');
  };

  const handleTournamentChange = (value) => {
    if (!value) {
      setSelectedTournamentId('');
      return;
    }
    setCompetitionType('tournament');
    setSelectedTournamentId(value);
    setSelectedLeagueId('');
  };

  const seasonOptions = (() => {
    const seasons = new Set();
    leagues.forEach((l) => l.season && seasons.add(l.season));
    tournaments.forEach((t) => t.season && seasons.add(t.season));
    if (selectedSeason) seasons.add(selectedSeason);
    return Array.from(seasons)
      .sort()
      .reverse()
      .map((season) => ({ value: season, label: `${season} Season` }));
  })();

  const selectedLeague = leagues.find(
    (league) => String(league.id) === String(selectedLeagueId)
  );
  const selectedTournament = tournaments.find(
    (tournament) => String(tournament.id) === String(selectedTournamentId)
  );
  const activeCompetition =
    competitionType === 'tournament' ? selectedTournament : selectedLeague;
  const hasSelection =
    (competitionType === 'league' && !!selectedLeagueId) ||
    (competitionType === 'tournament' && !!selectedTournamentId);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="bg-green-100 border-b border-green-200 shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg flex items-center justify-center shrink-0 overflow-hidden bg-transparent">
                <img
                  src="/tbf-logo-header.png"
                  alt="Tanzania Basketball Federation"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-3xl font-bold text-primary">Tanzania Basketball Portal</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Official portal for Tanzania Basketball Federation leagues and competitions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isAuthenticated && (
                <Link to="/admin-dashboard">
                  <Button variant="outline" iconName="LayoutDashboard" size="sm">
                    Admin Dashboard
                  </Button>
                </Link>
              )}
              {isAuthenticated ? (
                <Button 
                  variant="default" 
                  iconName="User" 
                  size="sm"
                  onClick={() => navigate('/profile')}
                >
                  My Profile
                </Button>
              ) : (
                <Button 
                  variant="default" 
                  iconName="LogIn" 
                  size="sm"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto py-8 px-4">
        {/* Navigation Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Link
            to="/regions"
            className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <Icon name="MapPin" size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Regions</h3>
            </div>
          </Link>

          <Link
            to="/clubs"
            className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-700 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Shield" size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Clubs</h3>
            </div>
          </Link>

          <Link
            to="/public-stats"
            className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <Icon name="BarChart3" size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Stats</h3>
            </div>
          </Link>

          <Link
            to="/match-center"
            className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Radio" size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Match Center</h3>
            </div>
          </Link>

          <Link
            to="/leagues"
            className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Trophy" size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Leagues</h3>
            </div>
          </Link>

          <Link
            to="/tournaments"
            className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-amber-600 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Award" size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Tournaments</h3>
            </div>
          </Link>
        </div>

        {error && (
          <div className="mb-6 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={18} />
              <p className="font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Competition Selector */}
        <div className="mb-8">
          <SearchAndFilters
            leagues={leagues}
            tournaments={tournaments}
            selectedLeagueId={selectedLeagueId}
            selectedTournamentId={selectedTournamentId}
            selectedSeason={selectedSeason}
            seasonOptions={seasonOptions}
            onLeagueChange={handleLeagueChange}
            onTournamentChange={handleTournamentChange}
            onSeasonChange={setSelectedSeason}
            onSearch={setSearchQuery}
            onFilterChange={setFilters}
            isLoadingCompetitions={isLoading}
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={48} className="text-primary animate-spin" />
            <p className="text-muted-foreground ml-4">Loading competition data...</p>
          </div>
        ) : !hasSelection ? (
          <div className="text-center py-12 bg-card rounded-lg border border-border card-shadow">
            <Icon name="Trophy" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Competition Selected</h3>
            <p className="text-muted-foreground">
              Please select a league or tournament to view fixtures, groups/games, and results.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Competition Overview Card */}
              <div className="bg-card rounded-lg border border-border card-shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-primary font-semibold mb-1">
                      {competitionType === 'tournament' ? 'Tournament' : 'League'}
                    </p>
                    <h2 className="text-2xl font-bold text-foreground">
                      {activeCompetition?.name}
                    </h2>
                    <p className="text-muted-foreground">{activeCompetition?.season || selectedSeason}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-3 py-1 rounded-full capitalize ${
                      (activeCompetition?.leagueStatus || activeCompetition?.tournamentStatus) === 'active'
                        ? 'bg-success/10 text-success'
                        : (activeCompetition?.leagueStatus || activeCompetition?.tournamentStatus) === 'upcoming'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {activeCompetition?.leagueStatus || activeCompetition?.tournamentStatus || 'upcoming'}
                    </span>
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">
                  {activeCompetition?.description || 'No description available.'}
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="bg-muted/30 rounded-lg p-3">
                    <Icon name="CalendarDays" size={20} className="text-primary mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Start Date</p>
                    <p className="font-medium text-foreground">{formatShortDate(activeCompetition?.startDate)}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <Icon name="CalendarX" size={20} className="text-destructive mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">End Date</p>
                    <p className="font-medium text-foreground">{formatShortDate(activeCompetition?.endDate)}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <Icon name="Users" size={20} className="text-accent mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Teams</p>
                    <p className="font-medium text-foreground">
                      {leagueStats.teamsCompeting}
                      {competitionType === 'league' && selectedLeague?.maxTeams
                        ? ` / ${selectedLeague.maxTeams}`
                        : ''}
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <Icon name="Basketball" size={20} className="text-success mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Games Played</p>
                    <p className="font-medium text-foreground">{leagueStats.totalGamesPlayed}</p>
                  </div>
                </div>
              </div>

              {/* Standings / Group tables */}
              <LeagueStandings 
                standings={standingsData} 
                selectedSeason={selectedSeason} 
                leagues={competitionType === 'league' ? leagues : []}
                selectedLeagueId={selectedLeagueId}
                onLeagueChange={handleLeagueChange}
                isLoading={isLoadingStandings}
                titlePrefix={competitionType === 'tournament' ? 'Group / Stage' : undefined}
              />

              {/* Upcoming Fixtures */}
              <UpcomingFixtures
                fixtures={upcomingFixtures}
                isLoading={isLoading}
                subtitle={
                  competitionType === 'tournament'
                    ? 'Upcoming tournament fixtures and group games'
                    : 'Next matches in the league'
                }
              />

              {/* Recent Results */}
              <RecentResults
                results={recentResults}
                isLoading={isLoading}
                competitionType={competitionType}
              />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-8">
              {/* About Tanzania Basketball Portal */}
              <div className="bg-card rounded-lg border border-border card-shadow p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Info" size={20} className="text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">About Tanzania Basketball Portal</h2>
                </div>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>
                    Welcome to the official Tanzania Basketball Federation (TBF) portal. 
                    Stay updated with the latest league standings, match fixtures, results, 
                    and team statistics.
                  </p>
                  <p>
                    Follow your favorite teams, track player performances, and never miss 
                    an important game in the Tanzania Basketball League.
                  </p>
                  <div className="pt-3 border-t border-border">
                    <p className="font-medium text-foreground mb-1">Contact TBF</p>
                    <p className="text-xs">Email: info[at]tbf.or.tz</p>
                    <p className="text-xs">Phone: +255 786 070762</p>
                  </div>
                </div>
              </div>

              {/* League Statistics */}
              <div className="bg-card rounded-lg border border-border card-shadow p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="BarChart3" size={20} className="text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">League Statistics</h2>
                </div>
                {isLoadingStats ? (
                  <div className="py-8 text-center">
                    <Icon name="Loader2" size={28} className="text-primary mx-auto mb-3 animate-spin" />
                    <p className="text-muted-foreground">Loading statistics...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground">Total Games Played</p>
                      <p className="font-medium text-foreground">{leagueStats.totalGamesPlayed}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground">Avg. Points per Game</p>
                      <p className="font-medium text-foreground">{leagueStats.averagePointsPerGame}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground">Top Scorer</p>
                      <p className="font-medium text-foreground">{leagueStats.topScorer || 'N/A'}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground">Teams Competing</p>
                      <p className="font-medium text-foreground">{leagueStats.teamsCompeting}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground">Unique Venues</p>
                      <p className="font-medium text-foreground">{leagueStats.venuesCount}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Follow TBF */}
              <div className="bg-card rounded-lg border border-border card-shadow p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Share2" size={20} className="text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">Follow TBF</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Stay connected with Tanzania Basketball Federation on social media 
                    and get the latest updates, news, and announcements.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a 
                      href="https://facebook.com/tzbasketball" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                    >
                      <Icon name="Share2" size={16} />
                      <span>Facebook</span>
                    </a>
                    <a 
                      href="https://twitter.com/tzbasketball" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors text-sm"
                    >
                      <Icon name="Share2" size={16} />
                      <span>Twitter</span>
                    </a>
                    <a 
                      href="https://instagram.com/tzbball" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors text-sm"
                    >
                      <Icon name="Share2" size={16} />
                      <span>Instagram</span>
                    </a>
                  </div>
                  <div className="pt-3 border-t border-border">
                    <p className="text-sm font-medium text-foreground mb-2">Newsletter</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Subscribe to receive updates about leagues, matches, and events.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="flex-1 px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <Button variant="default" size="sm" iconName="Mail">
                        Subscribe
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicLeaguePortal;
